import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Brain, Activity, AlertTriangle, Lightbulb, Trophy, Heart, RefreshCw, ChevronDown, ChevronUp, X, Sparkles, TrendingUp, Users, Stethoscope, MessageCircle, Phone, Clock, Send, Wallet, BarChart3, Search, Zap, Database, ImagePlus } from 'lucide-react';

const VeeranshAI = ({ API_BASE, theme, token, onClose, errorContext }) => {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeSection, setActiveSection] = useState('all');
  const [patientId, setPatientId] = useState('');
  const [patientAnalysis, setPatientAnalysis] = useState(null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [mainView, setMainView] = useState('insights');
  const [chatMessages, setChatMessages] = useState([{ role: 'veeransh', text: '🙏 Namaste Doctor! Main Veeransh hoon — Google Gemini ke saath. Clinic data, dental knowledge, ya koi bhi general question — sab poochh sakte ho. Internet search se bhi jawab de sakta hoon!' }]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatImage, setChatImage] = useState(null); // { base64, mimeType, previewUrl }
  const imageInputRef = useRef(null);
  const [recallData, setRecallData] = useState(null);
  const [predictionsData, setPredictionsData] = useState(null);
  const [peakHoursData, setPeakHoursData] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const scrollRef = useRef(null);
  const chatEndRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    loadInsights();
    loadPulse();
    loadTokenUsage();
  }, []);

  const loadInsights = async (forceFresh = false) => {
    setLoading(true); setLoadError(null);
    try {
      // Backend warm-up on a 7K-patient clinic can take ~14s for the first
      // call (Atlas Mumbai network latency × 8 collection scans). Give it
      // 90s before deciding the backend is dead. Subsequent calls hit the
      // 5-min server-side cache and return instantly.
      const url = `${API_BASE}/veeransh/insights${forceFresh ? '?refresh=1' : ''}`;
      const res = await axios.get(url, { ...authHeaders, timeout: 90000 });
      setData(res.data.veeransh);
    } catch (e) {
      console.error('Veeransh insights error:', e);
      const status = e.response?.status;
      const body = e.response?.data;
      if (status === 403 && body?.code === 'PLAN_UPGRADE_REQUIRED') {
        setLoadError(`Veeransh needs ${body.requiredPlans?.join(' or ')} plan. Aap abhi ${body.currentPlan} par ho — upgrade karo to use.`);
      } else if (status === 401) {
        setLoadError('Login session expired — re-login karo.');
      } else if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message)) {
        setLoadError('Bahut zyaada data hai analyze karne ke liye — server timeout ho gaya. "Retry" press karo, dobara try karega.');
      } else {
        setLoadError(body?.message || body?.error || `Backend not reachable (${status || e.message})`);
      }
    }
    setLoading(false);
  };

  const loadPulse = async () => {
    try {
      const res = await axios.get(`${API_BASE}/veeransh/pulse`, authHeaders);
      setPulse(res.data);
    } catch (e) { console.error('Veeransh pulse error:', e); }
  };

  const loadTokenUsage = async () => {
    try {
      const res = await axios.get(`${API_BASE}/veeransh/token-usage`, authHeaders);
      setTokenUsage(res.data);
    } catch (e) { console.error('Token usage error:', e); }
  };

  const quickSearch = async () => {
    if (!searchQuery.trim() || searchLoading) return;
    const query = searchQuery.trim();
    setSearchLoading(true);
    setSearchResult(null);
    try {
      const res = await axios.post(`${API_BASE}/veeransh/chat`, { message: query }, authHeaders);
      setSearchResult({ query, reply: res.data.reply, source: res.data.source, powered: res.data.powered });
      loadTokenUsage();
    } catch (e) {
      setSearchResult({ query, reply: 'Sorry, kuch error aa gaya. Dobara try karo.', source: 'error' });
    }
    setSearchLoading(false);
  };

  const analyzePatient = async (pid) => {
    if (!pid) return;
    setPatientLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/veeransh/patient/${pid}`, authHeaders);
      setPatientAnalysis(res.data);
    } catch (e) { console.error('Patient analysis error:', e); }
    setPatientLoading(false);
  };

  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(',')[1];
      setChatImage({ base64, mimeType: file.type, previewUrl: dataUrl, name: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const sendChat = async (override) => {
    const rawInput = typeof override === 'string' ? override : chatInput;
    if ((!rawInput.trim() && !chatImage) || chatLoading) return;
    const msg = rawInput.trim();
    const img = chatImage;
    setChatInput('');
    setChatImage(null);
    setChatMessages(prev => [...prev, { role: 'user', text: msg || '📷 X-ray / Image bheja', imagePreview: img?.previewUrl }]);
    setChatLoading(true);
    try {
      const payload = { message: msg || 'Is image ka dental analysis karo', errorContext };
      if (img) { payload.imageBase64 = img.base64; payload.imageMimeType = img.mimeType; }
      const res = await axios.post(`${API_BASE}/veeransh/chat`, payload, authHeaders);
      setChatMessages(prev => [...prev, { role: 'veeransh', text: res.data.reply, source: res.data.source, powered: res.data.powered }]);
      loadTokenUsage();
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'veeransh', text: 'Sorry, kuch error aa gaya. Dobara try karo.' }]);
    }
    setChatLoading(false);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // When the parent opens Veeransh with an error context, switch to chat
  // mode and fetch a proactive explanation from /error-assist.
  useEffect(() => {
    if (!errorContext) return;
    setMainView('chat');
    const label = errorContext.message || 'Error aaya hai';
    setChatMessages(prev => [
      ...prev,
      { role: 'user', text: `⚠️ Error: ${label}` },
      { role: 'veeransh', text: 'Ek sec, dekh raha hun kya hua...', pending: true },
    ]);
    (async () => {
      try {
        const r = await axios.post(`${API_BASE}/veeransh/error-assist`,
          { error: errorContext, userContext: { page: errorContext.page, action: errorContext.method } },
          authHeaders
        );
        setChatMessages(prev => [
          ...prev.filter(m => !m.pending),
          { role: 'veeransh', text: r.data.reply, source: r.data.source },
        ]);
      } catch {
        setChatMessages(prev => [
          ...prev.filter(m => !m.pending),
          { role: 'veeransh', text: 'Refresh karke dobara try karein (Cmd/Ctrl+R). Agar phir bhi aaye, mujhse specific details pooch lijiye.' },
        ]);
      }
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorContext]);

  const loadRecall = async () => {
    try { const res = await axios.get(`${API_BASE}/veeransh/recall`, authHeaders); setRecallData(res.data); } catch (e) { console.error(e); }
  };
  const loadPredictions = async () => {
    try { const res = await axios.get(`${API_BASE}/veeransh/predictions`, authHeaders); setPredictionsData(res.data); } catch (e) { console.error(e); }
  };
  const loadPeakHours = async () => {
    try { const res = await axios.get(`${API_BASE}/veeransh/peak-hours`, authHeaders); setPeakHoursData(res.data); } catch (e) { console.error(e); }
  };
  const loadPayments = async () => {
    try { const res = await axios.get(`${API_BASE}/veeransh/payments`, authHeaders); setPaymentsData(res.data); } catch (e) { console.error(e); }
  };

  const switchView = (view) => {
    setMainView(view);
    if (view === 'recall' && !recallData) loadRecall();
    if (view === 'predictions' && !predictionsData) loadPredictions();
    if (view === 'peakHours' && !peakHoursData) loadPeakHours();
    if (view === 'payments' && !paymentsData) loadPayments();
  };

  if (loading) {
    return (
      <div style={styles.container(theme)}>
        <div style={styles.loadingScreen}>
          <div style={styles.brainPulse}>
            <Brain size={48} color={theme.primary} />
          </div>
          <p style={{ color: theme.navy, fontSize: '1.1rem', fontWeight: 600, marginTop: 16 }}>
            Veeransh soch raha hai...
          </p>
          <p style={{ color: theme.textMuted, fontSize: '0.85rem' }}>App ka poora data analyze ho raha hai</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.container(theme)}>
        <div style={styles.header(theme)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={styles.avatarGlow(theme)}><Brain size={28} color="#fff" /></div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#fff', fontWeight: 700 }}>Veeransh</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>Offline mode</p>
            </div>
          </div>
          <button onClick={onClose} style={styles.refreshBtn}><X size={16} /></button>
        </div>
        <div style={{ padding: 24, textAlign: 'center', background: '#fff', color: theme.navy }}>
          <AlertTriangle size={40} color={theme.warning} style={{ margin: '0 auto 12px' }} />
          <p style={{ fontWeight: 600, margin: '0 0 6px' }}>Insights load nahi ho paaye</p>
          <p style={{ fontSize: 13, color: theme.textMuted, margin: '0 0 14px' }}>
            {loadError || 'Backend reachable nahi hai — chat abhi bhi kaam karega.'}
          </p>
          <button onClick={() => loadInsights(true)} style={{
            padding: '8px 18px', background: theme.primary, color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, marginRight: 8,
          }}>Retry</button>
          <button onClick={() => setData({ greeting: {}, summary: {}, insights: [], warnings: [], suggestions: [], achievements: [] })} style={{
            padding: '8px 18px', background: '#f1f5f9', color: theme.navy,
            border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600,
          }}>Open Chat Anyway</button>
        </div>
      </div>
    );
  }

  const { greeting, healthScore, summary, insights, warnings, suggestions, achievements } = data;

  return (
    <div style={styles.container(theme)} ref={scrollRef}>
      {/* Header */}
      <div style={styles.header(theme)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={styles.avatarGlow(theme)}>
            <Brain size={28} color="#fff" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#fff', fontWeight: 700 }}>Veeransh</h2>
            <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>Powered by Google Gemini</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setShowSearch(!showSearch)} style={{ ...styles.refreshBtn, background: showSearch ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)' }}>
            <Search size={16} />
          </button>
          <button onClick={() => { loadInsights(); loadPulse(); loadTokenUsage(); }} style={styles.refreshBtn}>
            <RefreshCw size={16} />
          </button>
          {onClose && (
            <button onClick={onClose} style={styles.refreshBtn}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ─── SEARCH BAR ─── */}
      {showSearch && (
        <div style={{ margin: '0', padding: '14px 16px', background: `linear-gradient(135deg, ${theme.surface}, ${theme.primaryLight || '#f0f9ff'})`, borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={16} color={theme.textMuted} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && quickSearch()}
                placeholder="Veeransh se kuch bhi poocho..."
                autoFocus
                style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 12, border: `2px solid ${theme.primary}40`, fontSize: '0.88rem', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
              />
            </div>
            <button onClick={quickSearch} disabled={searchLoading}
              style={{ width: 42, height: 42, borderRadius: 12, border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: searchLoading ? 0.6 : 1 }}>
              {searchLoading ? <RefreshCw size={16} color="#fff" style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} color="#fff" />}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {['Aaj ka summary?', 'Revenue kitna hua?', 'Pending treatments?', 'Best treatment?'].map((q, i) => (
              <button key={i} onClick={() => setSearchQuery(q)}
                style={{ padding: '3px 10px', borderRadius: 10, border: `1px solid ${theme.border}`, background: '#fff', fontSize: '0.7rem', color: theme.textMuted, cursor: 'pointer' }}>
                {q}
              </button>
            ))}
          </div>
          {searchResult && (
            <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 12, background: '#fff', border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Brain size={14} color={theme.primary} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.primary }}>Veeransh ka Jawab</span>
                {searchResult.powered && <span style={{ fontSize: '0.6rem', color: theme.textMuted, marginLeft: 'auto' }}>{searchResult.powered}</span>}
              </div>
              <p style={{ margin: 0, fontSize: '0.84rem', color: theme.navy, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{searchResult.reply}</p>
              <button onClick={() => { setSearchResult(null); setSearchQuery(''); }}
                style={{ marginTop: 8, padding: '3px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface, fontSize: '0.7rem', color: theme.textMuted, cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          )}
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ─── TOKEN USAGE METER ─── */}
      {tokenUsage && (
        <div style={{ margin: '12px 16px 0', padding: '12px 16px', borderRadius: 12, background: theme.surface, boxShadow: theme.shadowSm, border: `1px solid ${tokenUsage.status === 'critical' ? '#fecaca' : tokenUsage.status === 'warning' ? '#fef3c7' : theme.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={14} color={tokenUsage.status === 'critical' ? '#ef4444' : tokenUsage.status === 'warning' ? '#f59e0b' : theme.primary} />
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.navy }}>Gemini Token Usage</span>
            </div>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: tokenUsage.status === 'critical' ? '#fef2f2' : tokenUsage.status === 'warning' ? '#fffbeb' : '#f0fdf4', color: tokenUsage.status === 'critical' ? '#ef4444' : tokenUsage.status === 'warning' ? '#f59e0b' : '#10b981' }}>
              {tokenUsage.model}
            </span>
          </div>
          <div style={{ height: 10, borderRadius: 6, background: theme.border, overflow: 'hidden', marginBottom: 6 }}>
            <div style={{ width: `${Math.min(100, tokenUsage.daily.usedPercent)}%`, height: '100%', borderRadius: 6, background: tokenUsage.daily.usedPercent >= 90 ? 'linear-gradient(90deg, #ef4444, #dc2626)' : tokenUsage.daily.usedPercent >= 70 ? 'linear-gradient(90deg, #f59e0b, #d97706)' : `linear-gradient(90deg, ${theme.primary}, ${theme.primaryDark})`, transition: 'width 0.5s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: theme.textMuted }}><strong style={{ color: theme.navy }}>{tokenUsage.daily.usedPercent}%</strong> used today</span>
            <span style={{ fontSize: '0.72rem', color: theme.textMuted }}><strong style={{ color: '#10b981' }}>{tokenUsage.daily.remainingPercent}%</strong> baaki</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${theme.border}` }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: theme.primary }}>{(tokenUsage.daily.totalTokens || 0).toLocaleString()}</p>
              <p style={{ margin: 0, fontSize: '0.62rem', color: theme.textMuted }}>Aaj Tokens</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#10b981' }}>{(tokenUsage.daily.remainingTokens || 0).toLocaleString()}</p>
              <p style={{ margin: 0, fontSize: '0.62rem', color: theme.textMuted }}>Baaki Tokens</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#f59e0b' }}>{tokenUsage.daily.requests || 0}</p>
              <p style={{ margin: 0, fontSize: '0.62rem', color: theme.textMuted }}>Requests</p>
            </div>
          </div>
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${theme.border}`, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.65rem', color: theme.textMuted }}>
              <Database size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
              Total: <strong>{(tokenUsage.allTime.totalTokens || 0).toLocaleString()}</strong> tokens ({tokenUsage.allTime.requests} req)
            </span>
          </div>
        </div>
      )}

      {/* Greeting */}
      <div style={styles.greetingCard(theme)}>
        <p style={{ margin: 0, fontSize: '0.95rem', color: theme.navy, lineHeight: 1.5 }}>{greeting}</p>
      </div>

      {/* Health Score */}
      <div style={styles.healthScoreCard(theme, healthScore.color)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Clinic Health Score</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: '2.5rem', fontWeight: 800, color: healthScore.color === 'green' ? theme.success : healthScore.color === 'red' ? theme.danger : healthScore.color === 'orange' ? theme.warning : theme.info }}>
                {healthScore.score}
              </span>
              <span style={{ fontSize: '1rem', color: theme.textMuted }}>/100</span>
            </div>
            <p style={{ margin: '2px 0 0', fontSize: '0.85rem', fontWeight: 600, color: healthScore.color === 'green' ? theme.success : healthScore.color === 'red' ? theme.danger : healthScore.color === 'orange' ? theme.warning : theme.info }}>
              {healthScore.grade}
            </p>
          </div>
          <div style={styles.scoreCircle(theme, healthScore)}>
            <Heart size={24} color="#fff" />
          </div>
        </div>
        {healthScore.deductions.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
            <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: theme.textMuted, fontWeight: 600 }}>Score kyu kam hua:</p>
            {healthScore.deductions.map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: theme.danger, padding: '2px 0' }}>
                <span>{d.reason}</span>
                <span>-{d.points}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Pulse */}
      {pulse && (
        <div style={styles.pulseGrid}>
          <div style={styles.pulseCard(theme)}>
            <Users size={18} color={theme.primary} />
            <div>
              <p style={styles.pulseNumber(theme)}>{pulse.todayPatients}</p>
              <p style={styles.pulseLabel(theme)}>Aaj ke Patients</p>
            </div>
          </div>
          <div style={styles.pulseCard(theme)}>
            <Stethoscope size={18} color={theme.purple} />
            <div>
              <p style={styles.pulseNumber(theme)}>{pulse.todayAppointments}</p>
              <p style={styles.pulseLabel(theme)}>Aaj Appointments</p>
            </div>
          </div>
          <div style={styles.pulseCard(theme)}>
            <TrendingUp size={18} color={theme.success} />
            <div>
              <p style={styles.pulseNumber(theme)}>{pulse.todayRevenue?.toLocaleString('en-IN') || '0'}</p>
              <p style={styles.pulseLabel(theme)}>Aaj Revenue</p>
            </div>
          </div>
          <div style={styles.pulseCard(theme)}>
            <Activity size={18} color={theme.warning} />
            <div>
              <p style={styles.pulseNumber(theme)}>{pulse.pendingTreatments}</p>
              <p style={styles.pulseLabel(theme)}>Pending Plans</p>
            </div>
          </div>
        </div>
      )}

      {/* Tip of the moment */}
      {pulse?.tip && (
        <div style={styles.tipCard(theme)}>
          <Sparkles size={16} color={theme.primary} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ margin: 0, fontSize: '0.85rem', color: theme.navy, lineHeight: 1.5 }}>
            <strong>Veeransh Tip:</strong> {pulse.tip}
          </p>
        </div>
      )}

      {/* ─── MAIN VIEW TABS ─── */}
      <div style={{ ...styles.tabRow, flexWrap: 'wrap' }}>
        {[
          { key: 'insights', label: 'Insights', icon: Brain },
          { key: 'chat', label: 'Chat', icon: MessageCircle },
          { key: 'recall', label: 'Recall', icon: Phone },
          { key: 'predictions', label: 'Predictions', icon: Lightbulb },
          { key: 'peakHours', label: 'Peak Hours', icon: Clock },
          { key: 'payments', label: 'Payments', icon: Wallet },
        ].map(tab => (
          <button key={tab.key} onClick={() => switchView(tab.key)}
            style={styles.tabBtn(theme, mainView === tab.key)}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════ INSIGHTS VIEW ═══════ */}
      {mainView === 'insights' && (
        <div>
          {/* Sub-tabs */}
          <div style={{ ...styles.tabRow, marginTop: 8 }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'warnings', label: `Warnings (${warnings.length})` },
              { key: 'suggestions', label: `Tips (${suggestions.length})` },
              { key: 'achievements', label: `Wins (${achievements.length})` },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveSection(tab.key)}
                style={{ ...styles.tabBtn(theme, activeSection === tab.key), padding: '6px 12px', fontSize: '0.72rem' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Warnings */}
          {(activeSection === 'all' || activeSection === 'warnings') && warnings.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <h3 style={styles.sectionTitle(theme.danger)}><AlertTriangle size={16} /> Warnings</h3>
              {warnings.map((w, i) => (
                <div key={i} style={styles.alertCard(theme, 'warning')} onClick={() => toggleCard(`w${i}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                      <span style={{ fontSize: '1rem' }}>{w.icon}</span>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: theme.navy, fontWeight: 500, lineHeight: 1.4 }}>{w.message}</p>
                        {w.priority === 'high' && <span style={styles.priorityBadge(theme.danger)}>HIGH</span>}
                        {expandedCards[`w${i}`] && w.action && <div style={styles.actionBox(theme)}>{w.action}</div>}
                      </div>
                    </div>
                    {w.action && (expandedCards[`w${i}`] ? <ChevronUp size={14} color={theme.textMuted} /> : <ChevronDown size={14} color={theme.textMuted} />)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {(activeSection === 'all' || activeSection === 'suggestions') && suggestions.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <h3 style={styles.sectionTitle(theme.info)}><Lightbulb size={16} /> Suggestions</h3>
              {suggestions.map((s, i) => (
                <div key={i} style={styles.alertCard(theme, 'suggestion')} onClick={() => toggleCard(`s${i}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                      <span style={{ fontSize: '1rem' }}>{s.icon}</span>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: theme.navy, fontWeight: 500, lineHeight: 1.4 }}>{s.message}</p>
                        {expandedCards[`s${i}`] && s.action && <div style={styles.actionBox(theme)}>{s.action}</div>}
                      </div>
                    </div>
                    {s.action && (expandedCards[`s${i}`] ? <ChevronUp size={14} color={theme.textMuted} /> : <ChevronDown size={14} color={theme.textMuted} />)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Achievements */}
          {(activeSection === 'all' || activeSection === 'achievements') && achievements.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <h3 style={styles.sectionTitle(theme.success)}><Trophy size={16} /> Achievements</h3>
              {achievements.map((a, i) => (
                <div key={i} style={styles.achievementCard(theme)}>
                  <span>{a.icon}</span>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: theme.navy, fontWeight: 500 }}>{a.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* Insights */}
          {activeSection === 'all' && insights.map((ins, i) => (
            <div key={i} style={styles.insightCard(theme)}>
              <span>{ins.icon}</span>
              <div>
                <p style={{ margin: 0, fontSize: '0.7rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{ins.title}</p>
                <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: theme.navy, lineHeight: 1.4 }}>{ins.message}</p>
              </div>
            </div>
          ))}

          {/* Summary Stats */}
          {activeSection === 'all' && (
            <div style={styles.summaryGrid}>
              {[
                { label: 'Patients', value: summary.totalPatients, color: theme.primary },
                { label: 'Revenue', value: `₹${(summary.totalRevenue/1000).toFixed(0)}K`, color: theme.success },
                { label: 'Done', value: summary.doneTreatments, color: theme.info },
                { label: 'Planned', value: summary.plannedTreatments, color: theme.warning },
              ].map((s, i) => (
                <div key={i} style={styles.summaryItem(theme)}>
                  <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '0.65rem', color: theme.textMuted }}>{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════ CHAT VIEW ═══════ */}
      {mainView === 'chat' && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 240px)', minHeight: 300 }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
            {chatMessages.map((m, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 10,
              }}>
                <div style={{
                  maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
                  background: m.role === 'user'
                    ? `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`
                    : theme.surface,
                  color: m.role === 'user' ? '#fff' : theme.navy,
                  boxShadow: theme.shadowSm, fontSize: '0.85rem', lineHeight: 1.6,
                  borderBottomRightRadius: m.role === 'user' ? 4 : 14,
                  borderBottomLeftRadius: m.role === 'user' ? 14 : 4,
                  whiteSpace: 'pre-wrap',
                }}>
                  {m.role === 'veeransh' && <Brain size={14} color={theme.primary} style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                  {m.imagePreview && <img src={m.imagePreview} alt="uploaded" style={{ display: 'block', maxWidth: '100%', maxHeight: 160, borderRadius: 8, marginBottom: 6, objectFit: 'contain' }} />}
                  {m.text}
                  {m.powered && (
                    <p style={{ margin: '6px 0 0', fontSize: '0.65rem', fontStyle: 'italic',
                      color: m.source === 'kb_direct' ? '#16a34a' : theme.textMuted }}>
                      {m.source === 'kb_direct' ? '🧠 Veeransh Internal Knowledge' : `Powered by ${m.powered}`}
                    </p>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: 'flex', gap: 6, padding: '10px 16px' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.primary, animation: 'veeranshDot 1s infinite' }} />
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.primary, animation: 'veeranshDot 1s infinite 0.2s' }} />
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.primary, animation: 'veeranshDot 1s infinite 0.4s' }} />
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          {/* Quick questions */}
          <div style={{ padding: '4px 16px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['Aaj ka revenue?', 'Kitne patients?', 'Top treatments?', 'Koi suggestion?', '📷 X-ray upload karein'].map((q, i) => (
              <button key={i} onClick={() => { q.startsWith('📷') ? imageInputRef.current?.click() : sendChat(q); }}
                style={{ padding: '4px 10px', borderRadius: 12, border: `1px solid ${theme.border}`, background: theme.surface,
                  fontSize: '0.7rem', color: theme.textMuted, cursor: 'pointer' }}>
                {q}
              </button>
            ))}
          </div>
          {/* Chat input */}
          <div style={{ borderTop: `1px solid ${theme.border}` }}>
            {chatImage && (
              <div style={{ padding: '6px 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={chatImage.previewUrl} alt="preview" style={{ height: 48, width: 48, objectFit: 'cover', borderRadius: 8, border: `1px solid ${theme.border}` }} />
                <span style={{ fontSize: '0.75rem', color: theme.textMuted, flex: 1 }}>📷 {chatImage.name}</span>
                <button onClick={() => setChatImage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, padding: 2 }}>
                  <X size={14} />
                </button>
              </div>
            )}
            <div style={{ padding: '10px 16px', display: 'flex', gap: 8 }}>
              <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
              <button onClick={() => imageInputRef.current?.click()} title="X-ray / Photo upload"
                style={{
                  width: 42, height: 42, borderRadius: 12, border: `1px solid ${theme.border}`, cursor: 'pointer',
                  background: chatImage ? theme.primaryLight : theme.surface,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                <ImagePlus size={18} color={chatImage ? theme.primary : theme.textMuted} />
              </button>
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendChat()}
                placeholder={chatImage ? 'Image ke baare mein poocho (optional)...' : 'Veeransh se poocho...'}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 12, border: `1px solid ${theme.border}`,
                  fontSize: '0.88rem', outline: 'none', background: theme.surface,
                }}
              />
              <button onClick={sendChat} disabled={chatLoading}
                style={{
                  width: 42, height: 42, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <Send size={18} color="#fff" />
              </button>
            </div>
          </div>
          <style>{`@keyframes veeranshDot { 0%,100% { opacity:0.3; transform:scale(0.8); } 50% { opacity:1; transform:scale(1.2); } }`}</style>
        </div>
      )}

      {/* ═══════ RECALL VIEW ═══════ */}
      {mainView === 'recall' && (
        <div style={{ padding: '12px 16px' }}>
          <h3 style={styles.sectionTitle(theme.warning)}><Phone size={16} /> Patient Recall List</h3>
          <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: theme.textMuted }}>Ye patients ko call karo — wapas bulao clinic mein</p>
          {!recallData ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: 20 }}>Loading...</p> : (
            <>
              <div style={{ ...styles.insightCard(theme), background: theme.primaryLight, marginLeft: 0, marginRight: 0 }}>
                <span>📊</span>
                <p style={{ margin: 0, fontSize: '0.85rem', color: theme.navy }}><strong>{recallData.total}</strong> patients ko recall karna chahiye</p>
              </div>
              {recallData.patients?.slice(0, 20).map((p, i) => (
                <div key={i} style={{ ...styles.alertCard(theme, 'warning'), marginLeft: 0, marginRight: 0,
                  borderLeft: `3px solid ${p.priority === 'high' ? theme.danger : p.priority === 'medium' ? theme.warning : theme.info}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: theme.navy, fontWeight: 600 }}>{p.name}</p>
                      <p style={{ margin: '2px 0', fontSize: '0.75rem', color: theme.textMuted }}>{p.uhid} | {p.phone}</p>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: theme.navyLight }}>{p.reason}</p>
                    </div>
                    <span style={{ ...styles.priorityBadge(p.priority === 'high' ? theme.danger : p.priority === 'medium' ? theme.warning : theme.info) }}>
                      {p.daysSince}d
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ═══════ PREDICTIONS VIEW ═══════ */}
      {mainView === 'predictions' && (
        <div style={{ padding: '12px 16px' }}>
          <h3 style={styles.sectionTitle(theme.purple)}><Lightbulb size={16} /> Treatment Predictions</h3>
          <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: theme.textMuted }}>In patients ko ye treatment suggest karo</p>
          {!predictionsData ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: 20 }}>Loading...</p> : (
            <>
              <div style={{ ...styles.insightCard(theme), background: '#f5f3ff', marginLeft: 0, marginRight: 0 }}>
                <span>🔮</span>
                <p style={{ margin: 0, fontSize: '0.85rem', color: theme.navy }}><strong>{predictionsData.total}</strong> follow-up treatments suggest kiye jaa sakte hain</p>
              </div>
              {predictionsData.predictions?.slice(0, 20).map((p, i) => (
                <div key={i} style={{ ...styles.alertCard(theme, 'suggestion'), marginLeft: 0, marginRight: 0,
                  borderLeft: `3px solid ${p.urgency === 'high' ? theme.danger : p.urgency === 'medium' ? theme.warning : theme.info}` }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: theme.navy, fontWeight: 600 }}>{p.patientName || 'Patient'}</p>
                  <p style={{ margin: '2px 0', fontSize: '0.75rem', color: theme.textMuted }}>{p.uhid} | Tooth #{p.toothNumber}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.82rem', color: theme.navyLight }}>
                    <strong>{p.currentTreatment}</strong> ke baad <span style={{ color: theme.primary, fontWeight: 600 }}>{p.suggestedTreatment}</span> suggest karo
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: theme.textMuted, fontStyle: 'italic' }}>{p.reason}</p>
                  <span style={{ ...styles.priorityBadge(p.urgency === 'high' ? theme.danger : p.urgency === 'medium' ? theme.warning : theme.info), marginTop: 4 }}>
                    {p.urgency.toUpperCase()} | {p.daysSinceTreatment}d ago
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ═══════ PEAK HOURS VIEW ═══════ */}
      {mainView === 'peakHours' && (
        <div style={{ padding: '12px 16px' }}>
          <h3 style={styles.sectionTitle(theme.info)}><Clock size={16} /> Peak Hours Analysis</h3>
          {!peakHoursData ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: 20 }}>Loading...</p> : (
            <>
              <div style={{ ...styles.insightCard(theme), background: '#eff6ff', marginLeft: 0, marginRight: 0 }}>
                <span>💡</span>
                <p style={{ margin: 0, fontSize: '0.82rem', color: theme.navy, lineHeight: 1.5 }}>{peakHoursData.suggestion}</p>
              </div>
              <h4 style={{ margin: '16px 0 8px', fontSize: '0.85rem', color: theme.navy }}>Hourly Distribution</h4>
              {peakHoursData.hourlyDistribution?.map((h, i) => {
                const maxCount = Math.max(...(peakHoursData.hourlyDistribution || []).map(x => x.count));
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 80, fontSize: '0.75rem', color: theme.textMuted, textAlign: 'right' }}>{h.slot}</span>
                    <div style={{ flex: 1, height: 20, borderRadius: 6, background: theme.border, overflow: 'hidden' }}>
                      <div style={{ width: `${(h.count / maxCount) * 100}%`, height: '100%', borderRadius: 6,
                        background: h.count === maxCount ? `linear-gradient(90deg, ${theme.danger}, #f97316)` : `linear-gradient(90deg, ${theme.primary}, ${theme.primaryDark})`,
                      }} />
                    </div>
                    <span style={{ width: 24, fontSize: '0.75rem', color: theme.navy, fontWeight: 600 }}>{h.count}</span>
                  </div>
                );
              })}
              <h4 style={{ margin: '16px 0 8px', fontSize: '0.85rem', color: theme.navy }}>Day-wise Distribution</h4>
              {peakHoursData.dailyDistribution?.map((d, i) => {
                const maxDay = Math.max(...(peakHoursData.dailyDistribution || []).map(x => x.count));
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 40, fontSize: '0.78rem', color: theme.navy, fontWeight: 600 }}>{d.day}</span>
                    <div style={{ flex: 1, height: 22, borderRadius: 6, background: theme.border, overflow: 'hidden' }}>
                      <div style={{ width: maxDay ? `${(d.count / maxDay) * 100}%` : '0%', height: '100%', borderRadius: 6,
                        background: d.count === maxDay ? `linear-gradient(90deg, ${theme.success}, #059669)` : `linear-gradient(90deg, ${theme.info}, #2563eb)`,
                      }} />
                    </div>
                    <span style={{ width: 24, fontSize: '0.78rem', color: theme.navy, fontWeight: 600 }}>{d.count}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ═══════ PAYMENTS VIEW ═══════ */}
      {mainView === 'payments' && (
        <div style={{ padding: '12px 16px' }}>
          <h3 style={styles.sectionTitle(theme.success)}><Wallet size={16} /> Payment Tracking</h3>
          {!paymentsData ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: 20 }}>Loading...</p> : (
            <>
              {/* Alerts */}
              {paymentsData.alerts?.map((a, i) => (
                <div key={i} style={{ ...styles.alertCard(theme, 'warning'), marginLeft: 0, marginRight: 0 }}>
                  <span>{a.icon}</span> <span style={{ fontSize: '0.82rem', marginLeft: 6 }}>{a.message}</span>
                </div>
              ))}
              {/* Stats */}
              <div style={{ ...styles.pulseGrid, margin: '8px 0' }}>
                <div style={styles.pulseCard(theme)}>
                  <TrendingUp size={16} color={theme.success} />
                  <div>
                    <p style={styles.pulseNumber(theme)}>{paymentsData.totalRevenue?.toLocaleString('en-IN')}</p>
                    <p style={styles.pulseLabel(theme)}>Total Revenue</p>
                  </div>
                </div>
                <div style={styles.pulseCard(theme)}>
                  <BarChart3 size={16} color={theme.info} />
                  <div>
                    <p style={styles.pulseNumber(theme)}>{paymentsData.totalBills}</p>
                    <p style={styles.pulseLabel(theme)}>Total Bills</p>
                  </div>
                </div>
              </div>
              {/* Top Paying Patients */}
              <h4 style={{ margin: '16px 0 8px', fontSize: '0.85rem', color: theme.navy }}>Top Paying Patients</h4>
              {paymentsData.topPayingPatients?.slice(0, 10).map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', marginBottom: 4, borderRadius: 8, background: theme.surface, boxShadow: theme.shadowSm }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: i < 3 ? theme.warning : theme.border,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700,
                      color: i < 3 ? '#fff' : theme.textMuted }}>
                      {i + 1}
                    </span>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: theme.navy, fontWeight: 500 }}>{p.name}</p>
                      <p style={{ margin: 0, fontSize: '0.7rem', color: theme.textMuted }}>{p.uhid}</p>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: theme.success }}>
                    {p.totalSpent?.toLocaleString('en-IN')}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={styles.footer(theme)}>
        <Brain size={14} color={theme.textMuted} />
        <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>
          Veeransh v3.0 — Powered by Google Gemini
        </span>
      </div>
    </div>
  );
};

// ─── STYLES ───
const styles = {
  container: (theme) => ({
    height: '100%', overflowY: 'auto', backgroundColor: theme.bg,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }),
  loadingScreen: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100%', minHeight: 400,
  },
  brainPulse: {
    animation: 'pulse 1.5s ease-in-out infinite',
    width: 80, height: 80, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #E6F9FA, #f0f9ff)',
    boxShadow: '0 0 30px rgba(0,181,184,0.2)',
  },
  header: (theme) => ({
    background: `linear-gradient(135deg, ${theme.navy} 0%, ${theme.navyLight} 50%, ${theme.primaryDark} 100%)`,
    padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderRadius: '0 0 16px 16px',
  }),
  avatarGlow: (theme) => ({
    width: 48, height: 48, borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: `0 0 20px ${theme.primary}50`,
  }),
  refreshBtn: {
    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
    padding: 8, cursor: 'pointer', color: '#fff', display: 'flex',
  },
  greetingCard: (theme) => ({
    margin: '16px 16px 0', padding: '14px 16px', borderRadius: 12,
    background: theme.primaryLight, border: `1px solid ${theme.primary}30`,
  }),
  healthScoreCard: (theme, color) => ({
    margin: '12px 16px 0', padding: '16px 20px', borderRadius: 14,
    background: theme.surface, boxShadow: theme.shadowMd,
    borderLeft: `4px solid ${color === 'green' ? theme.success : color === 'red' ? theme.danger : color === 'orange' ? theme.warning : theme.info}`,
  }),
  scoreCircle: (theme, hs) => ({
    width: 56, height: 56, borderRadius: '50%',
    background: `linear-gradient(135deg, ${hs.color === 'green' ? theme.success : hs.color === 'red' ? theme.danger : hs.color === 'orange' ? theme.warning : theme.info}, ${hs.color === 'green' ? '#059669' : hs.color === 'red' ? '#dc2626' : hs.color === 'orange' ? '#d97706' : '#2563eb'})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: `0 4px 15px ${hs.color === 'green' ? theme.success : theme.danger}30`,
  }),
  pulseGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, margin: '12px 16px 0',
  },
  pulseCard: (theme) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
    borderRadius: 12, background: theme.surface, boxShadow: theme.shadowSm,
  }),
  pulseNumber: (theme) => ({ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: theme.navy }),
  pulseLabel: (theme) => ({ margin: 0, fontSize: '0.7rem', color: theme.textMuted }),
  tipCard: (theme) => ({
    margin: '12px 16px 0', padding: '12px 16px', borderRadius: 12,
    background: `linear-gradient(135deg, ${theme.primaryLight}, #f0f9ff)`,
    border: `1px solid ${theme.primary}20`,
    display: 'flex', gap: 10, alignItems: 'flex-start',
  }),
  tabRow: {
    display: 'flex', gap: 6, margin: '16px 16px 0', overflowX: 'auto', paddingBottom: 4,
  },
  tabBtn: (theme, active) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
    background: active ? theme.navy : theme.surface,
    color: active ? '#fff' : theme.textMuted,
    boxShadow: active ? theme.shadowMd : theme.shadowSm,
    transition: 'all 0.2s',
  }),
  sectionTitle: (color) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    margin: '16px 16px 8px', fontSize: '0.95rem', fontWeight: 700, color,
  }),
  alertCard: (theme, type) => ({
    margin: '0 16px 8px', padding: '14px 16px', borderRadius: 12,
    background: theme.surface, boxShadow: theme.shadowSm, cursor: 'pointer',
    borderLeft: `3px solid ${type === 'warning' ? theme.warning : theme.info}`,
    transition: 'box-shadow 0.2s',
  }),
  priorityBadge: (color) => ({
    display: 'inline-block', marginTop: 6, padding: '2px 8px', borderRadius: 10,
    fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: color,
    letterSpacing: 0.5,
  }),
  actionBox: (theme) => ({
    marginTop: 10, padding: '10px 14px', borderRadius: 8,
    background: theme.bg, fontSize: '0.83rem', color: theme.navyLight, lineHeight: 1.6,
    borderLeft: `3px solid ${theme.primary}`,
  }),
  achievementCard: (theme) => ({
    margin: '0 16px 8px', padding: '14px 16px', borderRadius: 12,
    background: `linear-gradient(135deg, #f0fdf4, #ecfdf5)`,
    boxShadow: theme.shadowSm, display: 'flex', gap: 10, alignItems: 'center',
    borderLeft: `3px solid ${theme.success}`,
  }),
  insightCard: (theme) => ({
    margin: '0 16px 8px', padding: '12px 16px', borderRadius: 12,
    background: theme.surface, boxShadow: theme.shadowSm,
    display: 'flex', gap: 12, alignItems: 'flex-start',
  }),
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, margin: '8px 16px 0',
  },
  summaryItem: (theme) => ({
    padding: '12px 8px', borderRadius: 10, background: theme.surface,
    boxShadow: theme.shadowSm, textAlign: 'center',
  }),
  footer: (theme) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '20px 16px', marginTop: 8,
  }),
};

export default VeeranshAI;
